import { Box } from '@material-ui/core';
import { LoanStatus, LoanStatusExternal, SinglePaymentStatus } from '@nemuru-eng/js-api-client';

import { CheckoutSessionLinks } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/CheckoutSessionLinks/CheckoutSessionLinks';
import { ProgressComponents } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/ProgressBarAndTimeline/_shared/ProgressComponents';
import { ProgressTimeline } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/ProgressBarAndTimeline/Progress/ProgressTimeline/ProgressTimeline';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { AbstractProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/AbstractProgressWorkflow';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { OrderProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/Progress/OrderProgress';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { HorizontalProgressBar } from '@/components/shared/components/HorizontalProgressBar';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { StyledAccordion } from '@/components/shared/components/StyledAccordion';
import { StatusTagType } from '@/config/types';
import { STYLE_PARAMS_BY_STATUS } from '@/constants/styleParamsByStatus';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface WithProgressProps {
  paymentMethodDetailEntity: PaymentMethodDetailEntity;
}

export const WithProgress = ({ paymentMethodDetailEntity }: WithProgressProps) => {
  const { palette } = useCustomTheme();
  const { userEntity } = useUserContext();

  const progress = paymentMethodDetailEntity.progress;

  if (progress instanceof AbstractProgressWorkflow) {
    throw new Error('Bad implementation of WithProgress with AbstractProgressWorkflow');
  }

  const getStatusName = (status: LoanStatus | LoanStatusExternal | SinglePaymentStatus) => {
    if (paymentMethodDetailEntity instanceof BnplPaymentMethodDetailEntity) {
      return OrderProgress.getStatusName(status as LoanStatus | LoanStatusExternal);
    }
    return status;
  };

  const desistedReason = progress?.getDesistedReason();

  return (
    <ProgressComponents.Layout
      StatusTag={
        <>
          {isValidProgress(progress) && (
            <StatusTag
              type={STYLE_PARAMS_BY_STATUS[progress.currentStep.status]?.colorPaletteName ?? StatusTagType.DEFAULT}
              colorType={STYLE_PARAMS_BY_STATUS[progress.currentStep.status]?.colorType}
              labelTextId={progress.currentStep.nameTextId}
            />
          )}
        </>
      }
      ProgressBar={
        <HorizontalProgressBar
          progressValue={progress?.progressPercentage ?? 0}
          color={
            progress?.isCompleted
              ? palette.success.main
              : progress?.isActive
              ? palette.secondary.main
              : palette.grey[500]
          }
          style={{ margin: 0 }}
        />
      }
    >
      <>
        <StyledAccordion.Accordion style={{ width: '100%' }}>
          <StyledAccordion.AccordionSummary
            disabled={!isValidProgress(progress)}
            style={{ marginTop: '0', display: 'flex', alignItems: 'center' }}
            expandIcon={<ProgressComponents.ExpandMoreIcon />}
          >
            {isValidProgress(progress) ? (
              <FlexRow width={'100%'} height={48}>
                <ProgressComponents.Status
                  isActive={progress.isActive}
                  title={progress.currentStep.nameTextId}
                  subtitle={
                    progress.lastStatusInCurrentStep
                      ? getStatusName(progress.lastStatusInCurrentStep!.status)
                      : undefined
                  }
                  subtitle2={desistedReason}
                />

                <ProgressComponents.HistoryButton />
              </FlexRow>
            ) : (
              <ProgressComponents.InvalidStatus />
            )}
          </StyledAccordion.AccordionSummary>
          <StyledAccordion.AccordionDetails>
            {isValidProgress(progress) && (
              <FlexColumn width={'100%'} gridGap={24}>
                <Box height={'100%'} width={'100%'} borderLeft={`2px solid ${palette.grey[200]}`} ml={1}>
                  <Box ml={1} mr={2}>
                    <ProgressTimeline progress={progress} />
                  </Box>
                </Box>
              </FlexColumn>
            )}
          </StyledAccordion.AccordionDetails>
        </StyledAccordion.Accordion>
        {userEntity && (
          <CheckoutSessionLinks
            paymentMethodDetailEntity={paymentMethodDetailEntity}
            userType={userEntity.user.userType}
          />
        )}
      </>
    </ProgressComponents.Layout>
  );
};

export const isValidProgress = (
  progress: AbstractProgress | undefined,
): progress is AbstractProgress & {
  statuses: NonNullable<AbstractProgress['statuses']>;
  progressStages: NonNullable<AbstractProgress['progressStages']>;
  currentStep: NonNullable<AbstractProgress['currentStep']>;
} => {
  return Boolean(progress && progress.statuses && progress.progressStages && progress.currentStep);
};
